<template>
    <div>
        <button
            style="margin-bottom: 15px"
            class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
            @click="router.push({ name: 'companyMatriculeWithUserList' })"
        >
            <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Retour
        </button>
        
        <h1 class="page-title">Liste des matricules utilisateur du client <span class="company-name">{{ companyName }}</span></h1>

        <Toast />
        <ConfirmDialog group="global" :closeOnEscape="false" :closable="false" />
    </div>

    <div class="card">
        <DataTable
            lazy
            removableSort
            paginator
            filterDisplay="row"
            tableStyle="min-width: 50rem"
            showGridlines
            stripedRows
            columnResizeMode="fit"
            :value="tableData"
            :rowHover="true"
            :rows="rows"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :loading="isLoading"
            :rowsPerPageOptions="rowsPerPageOptions"
            @page="onPaginate"
            @sort="onSort($event)"
        >
            <template #header>
                <div class="search-container">
                    <span class="p-input-icon-left">
                        <i class="pi pi-search" />
                        <InputText
                            v-model="globalSearch"
                            placeholder="Rechercher un client, matricule"
                            @keyup.enter="onGlobalFilterKeyEnter()"
                            @input="onGlobalFilterInput()"
                        />
                    </span>
                </div>
            </template>
            <Column field="userMatricule" header="Matricule" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.userMatricule }}</span>
                </template>
            </Column>
            <Column field="user.email" header="Email" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.user ? slotProps.data.user.email : '' }}</span>
                </template>
            </Column>
            <Column field="user.firstname" header="Client" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.user
                          ? `${slotProps.data.user.firstname} ${slotProps.data.user.lastname}`
                          : '' }}
                    </span>
                </template>
            </Column>
            <Column field="available" header="Activer / Désactiver" :showFilterMenu="false" sortable :style="{ textAlign: 'center' }">
                <template #body="slotProps">
                    <!-- Show switch only if no userEmail -->
                    <template v-if="!slotProps.data.user || !slotProps.data.user.email">
                        <InputSwitch
                            :model-value="slotProps.data.available"
                            @update:modelValue="(value) => confirmChangeAvailableDialog(value, slotProps.data)"
                        />
                    </template>
                </template>
            </Column>
            <Column header="Editer" :showFilterMenu="false">
                <template #body="slotProps">
                    <Button type="button"
                        @click="editCompanyMatricule(slotProps.data)"
                        icon="pi pi-pencil"
                        title="Modifier"
                        severity="info"
                        class="ml-2">
                    </Button>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, onUnmounted, ref} from "vue";
import DataTable from "primevue/datatable";
import swal from "@/_helpers/swal";
import {fetchUserMatriculesByCompany, toggleCompanyMatriculeAvailable} from "@/_services/companyMatricule/companyMatricule";
import {IUserMatriculeByCompany} from "@/models/companyMatricule/IUserMatriculeByCompany";
import {useCompanyMatriculeStore} from "@/stores/companyMatricule/companyMatriculeStore";
import {useToast} from 'primevue/usetoast';
import {onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter} from "vue-router";
import {useConfirm} from 'primevue/useconfirm';
import InputSwitch from "primevue/inputswitch";
import InputText from "primevue/inputtext";
import Column from "primevue/column";
import Button from "primevue/button";
import ConfirmDialog from "primevue/confirmdialog";

const toast = useToast();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();
const store = useCompanyMatriculeStore();
const isLoading = ref<boolean>(true);
const tableData = ref<IUserMatriculeByCompany[]>([]);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const currentPage = ref<number>(1);
const globalSearch = ref<string>("");
const modeSortable = ref<boolean>(false);
const isAvailable = ref<boolean | undefined>(undefined);
const companyName = ref<string>("");
type Sort = {
    fieldLabel: string;
    fieldValue: string;
};

const defaultsSort: Sort = {
    fieldLabel: "order[id]",
    fieldValue: "DESC",
};

const currentSort = ref<Sort | undefined>(undefined);

const companyUuidRouteParam = ref<string>('');

onMounted(async () => {
    companyUuidRouteParam.value = route.params.uuid as string;
    if (companyUuidRouteParam.value) {
        await populateDatatable(companyUuidRouteParam.value, rows.value, page.value, globalSearch.value, defaultsSort);
    } else {
        swal.error("Aucun identifiant de société trouvé dans l'URL.");
    }
    
    if (store.getSelectedCompany) {
        companyName.value = store.getSelectedCompany.name
    }
});

const populateDatatable = async (
    companyUuid: string,
    itemsPerPage: number,
    page: number,
    globalSearch?:string,
    sort?: Sort,
    available?: boolean
) => {
    showSpinner();
    
    const [isLoadingDt, totalRecordsDt, tableDataDt] = await fetchUserMatriculesByCompany(companyUuid, itemsPerPage, page, globalSearch, sort, available);
    isLoading.value = isLoadingDt;
    totalRecords.value = totalRecordsDt;
    tableData.value = tableDataDt;
    hideSpinner();
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    currentPage.value = e.page + 1;
    showSpinner();
    await populateDatatable(companyUuidRouteParam.value, e.rows, currentPage.value, globalSearch.value, currentSort.value, isAvailable.value);
};

const onGlobalFilterKeyEnter = () => {
    if (globalSearch.value.trim() != "") {
        showSpinner();
        populateDatatable(companyUuidRouteParam.value, rows.value, page.value, globalSearch.value);
    }
};

const onGlobalFilterInput = () => {
    if (globalSearch.value === "") {
        showSpinner();
        populateDatatable(companyUuidRouteParam.value, rows.value, page.value);
    }
};

const onSort = (e) => {
    showSpinner();
    if (e.sortField) {
        if (e.sortField !== "available") {
            currentSort.value = {
                fieldLabel: "order[" + e.sortField + "]",
                fieldValue: e.sortOrder === 1 ? "asc" : "desc",
            };

            isAvailable.value = undefined;
        } else {
            currentSort.value = undefined;
            isAvailable.value = e.sortOrder === 1;
        }
        
        modeSortable.value = true;
        populateDatatable(companyUuidRouteParam.value, rows.value, page.value, globalSearch.value, currentSort.value, isAvailable.value);
    } else {
        isAvailable.value = undefined;
        currentSort.value = undefined;
        modeSortable.value = false;
        populateDatatable(companyUuidRouteParam.value, rows.value, page.value, globalSearch.value, defaultsSort);
    }
};

const editCompanyMatricule = (companyMatricule: IUserMatriculeByCompany) => {
    store.setSelectedMatricule(companyMatricule);
    router.push({
        name: 'editCompanyMatricule',
        params: { uuid: companyMatricule.uuid }
    });
};

const confirmChangeAvailableDialog = async (newValue: boolean, data: IUserMatriculeByCompany) => {
    const currentStatus = data.available;
    let newStatus = currentStatus ? 'désactiver' : 'activer';
    
    confirm.require({
        group: 'global',
        message: `Êtes-vous sûr de vouloir ${newStatus} ce matricule pour cette société ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        acceptClass: 'my-custom-accept-class',
        accept: async () => {
            try {
                showSpinner();
                await toggleCompanyMatriculeAvailable(data).then((response) => {
                    if (!response) {
                        throw Error
                    }
                });
                toast.add({ severity: 'success', summary: 'Success', detail: 'Le matricule a bien été modifié !', life: 3000 });
                await populateDatatable(companyUuidRouteParam.value, rows.value, currentPage.value, globalSearch.value, currentSort.value, isAvailable.value);
            }  catch (error) {
                toast.add({ severity: 'error', summary: 'Error', detail: 'Une erreur est survenue.', life: 3000 });
                hideSpinner();
            }
        },
    });
};

onUnmounted(() => {
    store.clearSelectedCompany();
});

onBeforeRouteLeave(() => {
    store.clearSelectedCompany();
});

onBeforeRouteUpdate(() => {
    store.clearSelectedCompany();
});

const showSpinner = () => {
    isLoading.value = true;
};

const hideSpinner = () => {
    isLoading.value = false;
};
</script>

<style scoped>
    .company-name {
        color: #f5a22e;
    }
    
    ::v-deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider) {
        background: green;
    }

    ::v-deep(.p-inputswitch.p-inputswitch-checked:not(.p-disabled):hover .p-inputswitch-slider) {
        background: darkgreen;
    }


    ::v-deep(.p-inputswitch .p-inputswitch-slider) {
        background: red;
    }

    ::v-deep(.p-inputswitch:not(.p-disabled):hover .p-inputswitch-slider) {
        background: darkred;
    }
</style>